import React, { useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';
import ColumnConfigPanel from '../common/ColumnConfigPanel';
import { applyColumnOrder, loadColumnOrder, moveColumnInOrder, normalizeColumnOrder, saveColumnOrder } from '../../utils/columnLayout';

const getStorageKey = (discipline, projectId) => `${discipline}_${projectId}_visibleColumns`;
const getOrderStorageKey = (discipline, projectId) => `${discipline}_${projectId}_columnOrder`;

const DisciplineColumnConfigModal = React.memo(({ 
  isOpen, 
  onClose, 
  projectId, 
  discipline, 
  columns = [], // Array of column definitions from the discipline config
  visibleColumns, // Set of currently visible column keys
  onSave, 
  onVisibilityChange,
  columnOrder,
  onOrderChange,
}) => {
  const { instance, accounts } = useMsal();
  const [mappings, setMappings] = useState([]);
  const [localVisibleColumns, setLocalVisibleColumns] = useState(new Set());
  const [localOrder, setLocalOrder] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('visibility'); // 'visibility' or 'udf'

  useEffect(() => {
    if (isOpen && discipline) {
      fetchMappings();
      // Initialize visible columns from props or load from storage
      if (visibleColumns && visibleColumns.size > 0) {
        setLocalVisibleColumns(new Set(visibleColumns));
      } else {
        // Load from localStorage or default to columns with showInDashboard !== false
        const storageKey = getStorageKey(discipline, projectId);
        const saved = localStorage.getItem(storageKey);
        if (saved) {
          try {
            setLocalVisibleColumns(new Set(JSON.parse(saved)));
          } catch {
            setLocalVisibleColumns(new Set(columns.filter(c => c.showInDashboard !== false).map(c => c.key)));
          }
        } else {
          setLocalVisibleColumns(new Set(columns.filter(c => c.showInDashboard !== false).map(c => c.key)));
        }
      }
      const keys = columns.map(c => c.key);
      setLocalOrder(normalizeColumnOrder(columnOrder || loadColumnOrder(getOrderStorageKey(discipline, projectId)), keys));
      setError('');
    }
  }, [isOpen, discipline, projectId, visibleColumns, columns, columnOrder]);

  const fetchMappings = async () => {
    try {
      setLoading(true);
      setError('');
      // Piping uses a different API endpoint pattern than other disciplines
      const apiEndpoint = discipline === 'piping'
        ? `/matrix/project/${projectId}/column-mappings`
        : `/discipline-matrix/${discipline}/project/${projectId}/column-mappings`;
      const response = await authenticatedApiCall(
        instance,
        accounts,
        'GET',
        apiEndpoint
      );
      if (response.data.success) {
        const fetchedMappings = response.data.data || [];
        setMappings(fetchedMappings);
        // Ensure existing UDF columns are included in visibility
        if (fetchedMappings.length > 0) {
          setLocalVisibleColumns(prev => {
            const newSet = new Set(prev);
            fetchedMappings.forEach(m => newSet.add(m.column_key));
            return newSet;
          });
        }
      }
    } catch (err) {
      console.error('Error fetching mappings:', err);
      setMappings([]);
    } finally {
      setLoading(false);
    }
  };

  // Column Visibility handlers
  const handleToggleColumn = (columnKey) => {
    setLocalVisibleColumns(prev => {
      const newSet = new Set(prev);
      if (newSet.has(columnKey)) {
        // Keep at least one column visible
        if (newSet.size > 1) {
          newSet.delete(columnKey);
        }
      } else {
        newSet.add(columnKey);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    const allKeys = columns.map(c => c.key);
    // Add UDF columns that have mappings
    mappings.forEach(m => {
      if (!allKeys.includes(m.column_key)) {
        allKeys.push(m.column_key);
      }
    });
    setLocalVisibleColumns(new Set(allKeys));
  };

  const handleSelectNone = () => {
    // Keep at least the first column
    if (columns.length > 0) {
      setLocalVisibleColumns(new Set([columns[0].key]));
    }
  };

  // UDF handlers
  const handleAddMapping = () => {
    const usedColumns = new Set(mappings.map(m => m.column_key));
    for (let i = 1; i <= 10; i++) {
      const columnKey = `udf${String(i).padStart(2, '0')}`;
      if (!usedColumns.has(columnKey)) {
        setMappings([...mappings, {
          column_key: columnKey,
          custom_name: '',
          is_editable: true,
          original_excel_header: ''
        }]);
        setLocalVisibleColumns(prev => new Set([...prev, columnKey]));
        break;
      }
    }
  };

  const handleRemoveMapping = (index) => {
    const newMappings = [...mappings];
    const removedKey = newMappings[index].column_key;
    newMappings.splice(index, 1);
    setMappings(newMappings);
    // Also remove from visible columns
    setLocalVisibleColumns(prev => {
      const newSet = new Set(prev);
      newSet.delete(removedKey);
      return newSet;
    });
  };

  const handleMappingChange = (index, field, value) => {
    const newMappings = [...mappings];
    newMappings[index][field] = value;
    setMappings(newMappings);
  };

  const handleSave = async () => {
    setError('');
    
    // Validate UDF mappings only if there are any
    if (mappings.length > 0) {
      const customNames = mappings.map(m => m.custom_name.trim()).filter(n => n);
      const uniqueNames = new Set(customNames);
      if (customNames.length !== uniqueNames.size) {
        setError('Custom column names must be unique');
        return;
      }
      for (const mapping of mappings) {
        if (!mapping.custom_name || !mapping.custom_name.trim()) {
          setError('All mapped columns must have a custom name');
          return;
        }
      }
    }
    
    try {
      setSaving(true);
      
      // Save UDF mappings to backend only if there are mappings to save
      if (mappings.length > 0) {
        // Piping uses a different API endpoint pattern than other disciplines
        const apiEndpoint = discipline === 'piping'
          ? `/matrix/project/${projectId}/column-mappings`
          : `/discipline-matrix/${discipline}/project/${projectId}/column-mappings`;
        const response = await authenticatedApiCall(
          instance,
          accounts,
          'POST',
          apiEndpoint,
          { mappings }
        );
        
        if (!response.data.success) {
          setError(response.data.message || 'Failed to save column mappings');
          return;
        }
        
        // Notify parent of UDF changes
        if (onSave) onSave(response.data.data);
      }
      
      // Always save column visibility to localStorage
      const storageKey = getStorageKey(discipline, projectId);
      localStorage.setItem(storageKey, JSON.stringify([...localVisibleColumns]));
      saveColumnOrder(getOrderStorageKey(discipline, projectId), localOrder);
      
      // Notify parent of visibility changes
      if (onVisibilityChange) onVisibilityChange(localVisibleColumns);
      if (onOrderChange) onOrderChange(localOrder);
      
      onClose();
    } catch (err) {
      console.error('Error saving configuration:', err);
      console.error('Error response data:', err.response?.data);
      setError(err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };


  if (!isOpen) return null;

  const usedColumns = new Set(mappings.map(m => m.column_key));
  const canAddMore = usedColumns.size < 10;
  const disciplineName = discipline ? discipline.charAt(0).toUpperCase() + discipline.slice(1).replace(/-/g, ' ') : 'Discipline';

  // Get all displayable columns (standard + UDFs with mappings)
  const allDisplayableColumns = [
    ...columns,
    ...mappings.map(m => ({
      key: m.column_key,
      header: m.custom_name || m.column_key.toUpperCase()
    }))
  ];

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex',
      justifyContent: 'center', alignItems: 'center', zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white', borderRadius: '8px', padding: '24px',
        maxWidth: '700px', width: '95%', maxHeight: '85vh', overflow: 'auto'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '1.2rem' }}>⚙️</span> Column Configuration
          </h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#666' }}>×</button>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '0', marginBottom: '20px', borderBottom: '2px solid #e0e0e0' }}>
          <button
            onClick={() => setActiveTab('visibility')}
            style={{
              padding: '12px 20px',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              fontWeight: activeTab === 'visibility' ? 'bold' : 'normal',
              color: activeTab === 'visibility' ? '#dc3545' : '#666',
              borderBottom: activeTab === 'visibility' ? '2px solid #dc3545' : '2px solid transparent',
              marginBottom: '-2px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <span>◉</span> Column Visibility
          </button>
          <button
            onClick={() => setActiveTab('udf')}
            style={{
              padding: '12px 20px',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              fontWeight: activeTab === 'udf' ? 'bold' : 'normal',
              color: activeTab === 'udf' ? '#dc3545' : '#666',
              borderBottom: activeTab === 'udf' ? '2px solid #dc3545' : '2px solid transparent',
              marginBottom: '-2px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <span>+</span> Custom Columns ({mappings.length}/10)
          </button>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
            <div style={{ fontSize: '24px', marginBottom: '10px' }}>⏳</div>
            Loading...
          </div>
        ) : (
          <>
            {/* Column Visibility Tab */}
            {activeTab === 'visibility' && (
              <div>
                <div style={{ 
                  backgroundColor: '#e7f3ff', 
                  padding: '12px 16px', 
                  borderRadius: '6px', 
                  marginBottom: '16px',
                  color: '#0066cc',
                  fontSize: '0.9rem'
                }}>
                  <strong>Note:</strong> Show, hide, and rearrange columns. Your layout is saved on this browser.
                </div>

                <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', alignItems: 'center' }}>
                  <button
                    onClick={handleSelectAll}
                    style={{
                      padding: '8px 16px',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      background: '#007bff',
                      color: 'white',
                      fontWeight: 'bold'
                    }}
                  >
                    Select All
                  </button>
                  <button
                    onClick={handleSelectNone}
                    style={{
                      padding: '8px 16px',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      background: '#6c757d',
                      color: 'white',
                      fontWeight: 'bold'
                    }}
                  >
                    Select None
                  </button>
                  <span style={{ marginLeft: 'auto', color: '#666', fontSize: '0.9rem' }}>
                    {localVisibleColumns.size} of {allDisplayableColumns.length} columns visible
                  </span>
                </div>

                <ColumnConfigPanel
                  columns={applyColumnOrder(allDisplayableColumns, localOrder)}
                  isVisible={(key) => localVisibleColumns.has(key)}
                  onToggle={handleToggleColumn}
                  onMove={(from, to) => setLocalOrder(prev => moveColumnInOrder(normalizeColumnOrder(prev, allDisplayableColumns.map(c => c.key)), from, to))}
                  compact={false}
                  getLabel={(col) => col.header || col.label}
                />
              </div>
            )}

            {/* Custom Columns (UDF) Tab */}
            {activeTab === 'udf' && (
              <div>
                <p style={{ color: '#666', marginBottom: '20px' }}>
                  Configure up to 10 custom UDF columns for {disciplineName} tracking. These columns will appear in the matrix and exports.
                </p>

                {mappings.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '30px', color: '#666', backgroundColor: '#f9f9f9', borderRadius: '8px' }}>
                    <div style={{ fontSize: '32px', marginBottom: '10px' }}>📋</div>
                    No custom columns configured. Click "Add Column" to create one.
                  </div>
                ) : (
                  <div style={{ marginBottom: '20px' }}>
                    {mappings.map((mapping, index) => (
                      <div key={index} style={{
                        display: 'flex', gap: '10px', alignItems: 'center',
                        marginBottom: '10px', padding: '12px', backgroundColor: '#f9f9f9', borderRadius: '6px'
                      }}>
                        <div style={{ flex: '0 0 80px' }}>
                          <label style={{ fontSize: '11px', color: '#888', textTransform: 'uppercase' }}>Column</label>
                          <div style={{ fontWeight: 'bold', color: '#333' }}>{mapping.column_key.toUpperCase()}</div>
                        </div>
                        <div style={{ flex: 1 }}>
                          <label style={{ fontSize: '11px', color: '#888', textTransform: 'uppercase' }}>Custom Name *</label>
                          <input
                            type="text"
                            value={mapping.custom_name}
                            onChange={(e) => handleMappingChange(index, 'custom_name', e.target.value)}
                            placeholder="Enter column name"
                            style={{ 
                              width: '100%', 
                              padding: '10px', 
                              border: '1px solid #ddd', 
                              borderRadius: '4px',
                              fontSize: '14px'
                            }}
                          />
                        </div>
                        <button
                          onClick={() => handleRemoveMapping(index)}
                          style={{
                            background: '#dc3545', color: 'white', border: 'none',
                            borderRadius: '4px', padding: '10px 14px', cursor: 'pointer',
                            fontWeight: 'bold'
                          }}
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {canAddMore && (
                  <button
                    onClick={handleAddMapping}
                    style={{
                      background: '#28a745', color: 'white', border: 'none',
                      borderRadius: '4px', padding: '12px 24px', cursor: 'pointer', 
                      marginBottom: '20px', fontWeight: 'bold',
                      display: 'flex', alignItems: 'center', gap: '8px'
                    }}
                  >
                    <span style={{ fontSize: '18px' }}>+</span> Add Column ({10 - usedColumns.size} remaining)
                  </button>
                )}
              </div>
            )}

            {error && (
              <div style={{ color: '#dc3545', backgroundColor: '#f8d7da', padding: '12px', borderRadius: '6px', marginBottom: '20px', marginTop: '16px' }}>
                ⚠️ {error}
              </div>
            )}

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px', paddingTop: '16px', borderTop: '1px solid #e0e0e0' }}>
              <button
                onClick={onClose}
                style={{ 
                  padding: '12px 24px', 
                  border: '1px solid #ddd', 
                  borderRadius: '4px', 
                  cursor: 'pointer', 
                  background: '#6c757d',
                  color: 'white',
                  fontWeight: 'bold'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                style={{
                  padding: '12px 24px', 
                  border: 'none', 
                  borderRadius: '4px',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  background: saving ? '#6c757d' : '#007bff', 
                  color: 'white',
                  fontWeight: 'bold'
                }}
              >
                {saving ? 'Saving...' : 'Save Configuration'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
});

export default DisciplineColumnConfigModal;
